@if (!meta()) {
  <p class="px-4 py-3 text-[0.85rem] text-wb-secondary">Loading metadata...</p>
} @else {

  <div class="px-4 pt-3 pb-2 bg-[var(--wb-bg-surface)]/50">
    <div class="text-[0.6875rem] font-semibold text-wb-muted uppercase tracking-[0.1em] mb-2 font-mono">Region</div>
    <select
      class="w-full bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2.5 outline-none font-mono cursor-pointer focus:ring-2 focus:ring-wb-accent/20"
      [ngModel]="region()"
      (ngModelChange)="regionChange.emit($event)"
    >
      <option value="AWS">AWS</option>
      <option value="DEFAULT">VOLCANO</option>
    </select>
  </div>

  <div class="px-4 pt-3 pb-2 bg-[var(--wb-bg-elevated)]">
    <div class="text-[0.6875rem] font-semibold text-wb-muted uppercase tracking-[0.1em] mb-2 font-mono">Chapters</div>
    @if (meta()!.chapters.length === 0) {
      <span class="text-[0.82rem] text-wb-muted italic">No chapters</span>
    } @else {
      <p-multiselect
        [options]="meta()!.chapters"
        [ngModel]="chapters()"
        (ngModelChange)="chaptersChange.emit($event)"
        placeholder="Select chapters..."
        appendTo="body"
        styleClass="config-multiselect"
      />
    }
  </div>

  <!-- ── Tab Switcher ───────────────────────────────────────────────── -->
  <div class="flex border-b border-[var(--wb-border)]/30">
    <button
      class="flex-1 text-[0.78rem] font-semibold uppercase tracking-[0.08em] py-2.5 bg-transparent border-0 cursor-pointer transition-colors font-mono"
      [class.text-wb-accent]="activeTab() === 'values'"
      [class.text-wb-muted]="activeTab() !== 'values'"
      [class.border-b-2]="activeTab() === 'values'"
      [style.border-color]="activeTab() === 'values' ? 'var(--wb-accent)' : 'transparent'"
      (click)="activeTab.set('values')"
    >Values</button>
    <button
      class="flex-1 text-[0.78rem] font-semibold uppercase tracking-[0.08em] py-2.5 bg-transparent border-0 cursor-pointer transition-colors font-mono"
      [class.text-wb-accent]="activeTab() === 'defs'"
      [class.text-wb-muted]="activeTab() !== 'defs'"
      [class.border-b-2]="activeTab() === 'defs'"
      [style.border-color]="activeTab() === 'defs' ? 'var(--wb-accent)' : 'transparent'"
      (click)="activeTab.set('defs')"
    >Definitions</button>
  </div>

  @if (activeTab() === 'defs') {
    <!-- ── Definitions: Arguments + Variables ─────────────────────────── -->
    <div class="px-4 pt-3 pb-2 bg-[var(--wb-bg-surface)]/50">
      <div class="flex items-center justify-between mb-2">
        <div class="text-[0.6875rem] font-semibold text-wb-muted uppercase tracking-[0.1em] font-mono">Arguments</div>
        <button class="text-[0.75rem] text-wb-accent hover:text-wb-accent-2 transition-colors bg-transparent border-0 cursor-pointer" (click)="openNewArgDialog()">
          <i class="pi pi-plus mr-1"></i> New
        </button>
      </div>
      @if (argDefs().length === 0) {
        <span class="text-[0.82rem] text-wb-muted italic">No argument definitions</span>
      }
      @for (def of argDefs(); track def.key) {
        <div class="flex items-center gap-2 mb-1.5 group">
          <span class="flex-1 min-w-0 text-[0.82rem] font-mono text-wb-accent-2 whitespace-nowrap overflow-hidden text-ellipsis" [title]="def.description ?? def.key">{{ def.key }}</span>
          <span class="text-[0.6875rem] text-wb-muted shrink-0">{{ def.type }}</span>
          @if (isMalformedKey(def.key)) {
            <i class="pi pi-exclamation-triangle text-amber-400 text-[0.7rem] shrink-0"
               pTooltip="This key contains a character that is not valid in an identifier (most often a space). It is passed verbatim into generated embed code and CSV headers. Renaming it would break embed codes already published, so fix it only if you can update every consumer."
               tooltipPosition="left"></i>
          }
          @if (def.type === 'variant') {
            <span class="text-[0.6rem] text-wb-muted bg-[var(--wb-bg-surface)] rounded px-1.5 py-0.5 shrink-0">variant</span>
          } @else {
            <button class="text-wb-muted hover:text-wb-text transition-colors bg-transparent border-0 cursor-pointer opacity-0 group-hover:opacity-100 text-[0.8rem]" (click)="openEditArgDialog(def)" title="Edit">
              <i class="pi pi-pencil"></i>
            </button>
            <button class="text-wb-muted hover:text-red-400 transition-colors bg-transparent border-0 cursor-pointer opacity-0 group-hover:opacity-100 text-[0.8rem]" (click)="openDeleteArgDialog(def)" title="Delete">
              <i class="pi pi-trash"></i>
            </button>
          }
        </div>
      }
    </div>

    <div class="px-4 pt-3 pb-2 bg-[var(--wb-bg-elevated)]">
      <div class="flex items-center justify-between mb-2">
        <div class="text-[0.6875rem] font-semibold text-wb-muted uppercase tracking-[0.1em] font-mono">Variables</div>
        <button class="text-[0.75rem] text-wb-accent hover:text-wb-accent-2 transition-colors bg-transparent border-0 cursor-pointer" (click)="openNewVarDialog()">
          <i class="pi pi-plus mr-1"></i> New
        </button>
      </div>
      @if (varDefs().length === 0) {
        <span class="text-[0.82rem] text-wb-muted italic">No variable definitions</span>
      }
      @for (def of varDefs(); track def.key) {
        <div class="flex items-center gap-2 mb-1.5 group">
          <span class="flex-1 min-w-0 text-[0.82rem] font-mono text-wb-accent-2 whitespace-nowrap overflow-hidden text-ellipsis" [title]="def.description ?? def.key">{{ def.key }}</span>
          <span class="text-[0.6875rem] text-wb-muted shrink-0">{{ def.type }}</span>
          @if (isMalformedKey(def.key)) {
            <i class="pi pi-exclamation-triangle text-amber-400 text-[0.7rem] shrink-0"
               pTooltip="This key contains a character that is not valid in an identifier (most often a space). It is passed verbatim into generated embed code and CSV headers. Renaming it would break embed codes already published, so fix it only if you can update every consumer."
               tooltipPosition="left"></i>
          }
          <button class="text-wb-muted hover:text-wb-text transition-colors bg-transparent border-0 cursor-pointer opacity-0 group-hover:opacity-100 text-[0.8rem]" (click)="openEditVarDialog(def)" title="Edit">
            <i class="pi pi-pencil"></i>
          </button>
          <button class="text-wb-muted hover:text-red-400 transition-colors bg-transparent border-0 cursor-pointer opacity-0 group-hover:opacity-100 text-[0.8rem]" (click)="openDeleteVarDialog(def)" title="Delete">
            <i class="pi pi-trash"></i>
          </button>
        </div>
      }
    </div>
  } @else {
    <!-- ── Values: Arguments + Variables ──────────────────────────────── -->
    <div class="px-4 pt-3 pb-2 bg-[var(--wb-bg-surface)]/50">
      <div class="text-[0.6875rem] font-semibold text-wb-muted uppercase tracking-[0.1em] mb-2 font-mono">Arguments</div>
      @if (meta()!.args.length === 0) {
        <span class="text-[0.82rem] text-wb-muted italic">No arguments defined</span>
      }
      @for (arg of args(); track arg.key; let i = $index) {
        <div class="flex items-center gap-2 mb-2">
          <span class="w-[80px] shrink-0 text-[0.82rem] text-wb-accent-2 font-mono whitespace-nowrap overflow-hidden text-ellipsis" [title]="arg.type">{{ arg.key }}</span>
          <span class="text-[0.6875rem] text-wb-muted w-[44px] shrink-0">{{ arg.type }}</span>
          @if (arg.type === 'boolean') {
            <select
              class="flex-1 min-w-0 bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2 outline-none font-mono cursor-pointer focus:ring-2 focus:ring-wb-accent/20"
              [ngModel]="arg.value"
              (ngModelChange)="argChange.emit({ index: i, value: $event })"
            >
              <option value="" class="text-wb-muted italic">undefined</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          } @else if ((arg.options?.length ?? 0) > 0) {
            <select
              class="flex-1 min-w-0 bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2 outline-none font-mono cursor-pointer focus:ring-2 focus:ring-wb-accent/20"
              [ngModel]="arg.value"
              (ngModelChange)="argChange.emit({ index: i, value: $event })"
            >
              <option value="" class="text-wb-muted italic">undefined</option>
              @for (opt of arg.options; track opt) {
                <option [value]="opt">{{ opt }}</option>
              }
            </select>
          } @else {
            <input
              class="flex-1 min-w-0 bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2 outline-none font-mono focus:ring-2 focus:ring-wb-accent/20"
              [ngModel]="arg.value"
              (ngModelChange)="argChange.emit({ index: i, value: $event })"
              [placeholder]="arg.type"
            />
          }
        </div>
      }
    </div>

    <div class="px-4 pt-3 pb-2 bg-[var(--wb-bg-elevated)]">
      <div class="text-[0.6875rem] font-semibold text-wb-muted uppercase tracking-[0.1em] mb-2 font-mono">Variables</div>
      @if (meta()!.vars.length === 0) {
        <span class="text-[0.82rem] text-wb-muted italic">No variables defined</span>
      }
      @for (v of vars(); track v.key; let i = $index) {
        <div class="flex items-center gap-2 mb-2">
          <span class="w-[80px] shrink-0 text-[0.82rem] text-wb-accent-2 font-mono whitespace-nowrap overflow-hidden text-ellipsis" [title]="v.type">{{ v.key }}</span>
          <span class="text-[0.6875rem] text-wb-muted w-[44px] shrink-0">{{ v.type }}</span>
          @if (v.type === 'boolean') {
            <select
              class="flex-1 min-w-0 bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2 outline-none font-mono cursor-pointer focus:ring-2 focus:ring-wb-accent/20"
              [ngModel]="v.value"
              (ngModelChange)="varChange.emit({ index: i, value: $event })"
            >
              <option value="" class="text-wb-muted italic">undefined</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          } @else if ((v.options?.length ?? 0) > 0) {
            <select
              class="flex-1 min-w-0 bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2 outline-none font-mono cursor-pointer focus:ring-2 focus:ring-wb-accent/20"
              [ngModel]="v.value"
              (ngModelChange)="varChange.emit({ index: i, value: $event })"
            >
              <option value="" class="text-wb-muted italic">undefined</option>
              @for (opt of v.options; track opt) {
                <option [value]="opt">{{ opt }}</option>
              }
            </select>
          } @else {
            <input
              class="flex-1 min-w-0 bg-[var(--wb-bg-surface)] border-0 rounded-xl text-wb-text text-[0.82rem] px-4 py-2 outline-none font-mono focus:ring-2 focus:ring-wb-accent/20"
              [ngModel]="v.value"
              (ngModelChange)="varChange.emit({ index: i, value: $event })"
              [placeholder]="v.type"
            />
          }
        </div>
      }
    </div>
  }

}

<!-- ── Create/Edit Def Dialog ──────────────────────────────────────── -->
<p-dialog
  [header]="defDialogTitle"
  [visible]="defDialogVisible()"
  (visibleChange)="defDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '400px' }"
  appendTo="body"
>
  <div class="flex flex-col gap-3 pt-2">
    <div>
      <label class="text-[0.75rem] text-wb-muted block mb-1">Key</label>
      <input
        pInputText
        class="w-full"
        [ngModel]="defDialogKey()"
        (ngModelChange)="defDialogKey.set($event)"
        [disabled]="!isCreateDialog"
        placeholder="e.g. gender"
      />
      @if (defDialogKeyError) {
        <small class="text-red-400 mt-1 block">{{ defDialogKeyError }}</small>
      } @else if (defDialogKeyWarning) {
        <small class="text-amber-400 mt-1 block">{{ defDialogKeyWarning }}</small>
      } @else if (isCreateDialog) {
        <small class="text-wb-muted mt-1 block">Lower case, no spaces — underscores and hyphens are fine.</small>
      }
    </div>
    <div>
      <label class="text-[0.75rem] text-wb-muted block mb-1">Type</label>
      <select
        class="w-full bg-[var(--wb-bg-surface)] border border-[var(--wb-border)] rounded-lg text-wb-text text-[0.85rem] px-3 py-2 outline-none"
        [ngModel]="defDialogType()"
        (ngModelChange)="onDefDialogTypeChange($event)"
      >
        <option value="string">string</option>
        <option value="number">number</option>
        <option value="boolean">boolean</option>
      </select>
    </div>
    @if (isArgDialog && defDialogType() === 'string') {
      <div>
        <label class="text-[0.75rem] text-wb-muted block mb-1">Options (comma-separated)</label>
        <input
          pInputText
          class="w-full"
          [ngModel]="defDialogOptions()"
          (ngModelChange)="defDialogOptions.set($event)"
          placeholder="e.g. male, female"
        />
      </div>
    }
    <div>
      <label class="text-[0.75rem] text-wb-muted block mb-1">Default Value</label>
      @if (defDialogType() === 'boolean') {
        <p-select class="w-full" [options]="booleanOptions" optionLabel="label" optionValue="value"
                  [ngModel]="defDialogDefaultValue()" (ngModelChange)="defDialogDefaultValue.set($event)" />
      } @else if (defDialogType() === 'number') {
        <p-inputnumber class="w-full" [ngModel]="defDialogDefaultValue()" (ngModelChange)="defDialogDefaultValue.set($event)" />
      } @else {
        <input pInputText class="w-full" [ngModel]="defDialogDefaultValue()" (ngModelChange)="defDialogDefaultValue.set($event)" />
      }
    </div>
    <div>
      <label class="text-[0.75rem] text-wb-muted block mb-1">Description</label>
      <input
        pInputText
        class="w-full"
        [ngModel]="defDialogDescription()"
        (ngModelChange)="defDialogDescription.set($event)"
      />
    </div>
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2 pt-2">
      <p-button label="Cancel" severity="secondary" (click)="defDialogVisible.set(false)" />
      <p-button [label]="isCreateDialog ? 'Create' : 'Save'" [disabled]="!defDialogValid" (click)="confirmDefDialog()" />
    </div>
  </ng-template>
</p-dialog>

<!-- ── Delete Def Dialog ──────────────────────────────────────────── -->
<p-dialog
  header="Delete {{ deleteDialogKind() === 'arg' ? 'argument' : 'variable' }}"
  [visible]="deleteDialogVisible()"
  (visibleChange)="deleteDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '420px' }"
  appendTo="body"
>
  <div class="pt-2">
    @if (deleteDialogLoading()) {
      <p class="text-wb-muted text-[0.85rem]">Checking usage...</p>
    } @else if (deleteDialogUsage(); as usage) {
      @if (usage.count === 0) {
        <p class="text-wb-text text-[0.85rem]">
          <strong>{{ deleteDialogKey() }}</strong> is not used anywhere. Delete it?
        </p>
      } @else {
        <p class="text-wb-text text-[0.85rem] mb-2">
          <strong>{{ deleteDialogKey() }}</strong> is currently used by <strong>{{ usage.count }}</strong> object{{ usage.count === 1 ? '' : 's' }}:
        </p>
        <div class="max-h-[200px] overflow-y-auto bg-[var(--wb-bg-surface)] rounded-lg p-3 text-[0.8rem] font-mono">
          @for (loc of usage.locations; track $index) {
            <div class="text-wb-muted py-0.5">
              {{ loc.type }}
              @if (loc.chapterSlug) { / {{ loc.chapterSlug }} }
              @if (loc.slug) { / {{ loc.slug }} }
              @if (loc.contentItemId) { / {{ loc.contentItemId }} }
              @if (loc.actionId) { / {{ loc.actionId }} }
            </div>
          }
        </div>
        <p class="text-[0.8rem] text-wb-muted mt-2">Remove all usages first before deleting this definition.</p>
      }
    }
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2 pt-2">
      <p-button label="Cancel" severity="secondary" (click)="deleteDialogVisible.set(false)" />
      @if (!deleteDialogLoading() && deleteDialogUsage()?.count === 0) {
        <p-button label="Delete" severity="danger" (click)="confirmDelete()" />
      }
    </div>
  </ng-template>
</p-dialog>
